import React from "react";

interface PreparationMetrics {
  financialReadiness: number;
  scheduleReadiness: number;
  requirementsMaturity: number;
  technicalFoundation: number;
  organizationalReadiness: number;
  assessment:
    | "Well Prepared"
    | "Nearly Ready"
    | "Preparation Needed"
    | "Build Foundation First";
  recommendation: string;
}

interface PreparationPentagonProps {
  metrics: PreparationMetrics;
}

// Color functions for Preparation Model (solution-focused)
const getReadinessColor = (readiness: number): string => {
  if (readiness >= 70) return "#10b981"; // Green - well prepared
  if (readiness >= 50) return "#3b82f6"; // Blue - nearly ready
  if (readiness >= 30) return "#f59e0b"; // Amber - needs work
  return "#f97316"; // Orange - build foundation
};

const getAssessmentColor = (
  assessment: PreparationMetrics["assessment"],
): string => {
  switch (assessment) {
    case "Well Prepared":
      return "#10b981";
    case "Nearly Ready":
      return "#3b82f6";
    case "Preparation Needed":
      return "#f59e0b";
    case "Build Foundation First":
      return "#f97316";
  }
};

export const PreparationPentagon: React.FC<PreparationPentagonProps> = ({
  metrics,
}) => {
  const centerX = 225;
  const centerY = 225;
  const maxRadius = 120;

  // Pentagon vertices (5 points, starting from top, going clockwise)
  const getVertex = (index: number, value: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    const radius = (value / 100) * maxRadius;
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  // Reference pentagon (max values)
  const refVertices = [0, 1, 2, 3, 4].map((i) => getVertex(i, 100));
  const refPath =
    refVertices
      .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
      .join(" ") + " Z";

  // Data pentagon
  const dataValues = [
    metrics.financialReadiness,
    metrics.scheduleReadiness,
    metrics.requirementsMaturity,
    metrics.technicalFoundation,
    metrics.organizationalReadiness,
  ];

  const dataVertices = dataValues.map((value, index) =>
    getVertex(index, value),
  );
  const dataPath =
    dataVertices
      .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
      .join(" ") + " Z";

  // Labels and icons for each vertex
  const labels = [
    {
      text: "Financial",
      icon: "💰",
      color: getReadinessColor(metrics.financialReadiness),
      value: metrics.financialReadiness,
      display: `${Math.round(metrics.financialReadiness)}%`,
    },
    {
      text: "Schedule",
      icon: "⏰",
      color: getReadinessColor(metrics.scheduleReadiness),
      value: metrics.scheduleReadiness,
      display: `${Math.round(metrics.scheduleReadiness)}%`,
    },
    {
      text: "Requirements",
      icon: "📋",
      color: getReadinessColor(metrics.requirementsMaturity),
      value: metrics.requirementsMaturity,
      display: `${Math.round(metrics.requirementsMaturity)}%`,
    },
    {
      text: "Technical",
      icon: "🔧",
      color: getReadinessColor(metrics.technicalFoundation),
      value: metrics.technicalFoundation,
      display: `${Math.round(metrics.technicalFoundation)}%`,
    },
    {
      text: "Organization",
      icon: "🏢",
      color: getReadinessColor(metrics.organizationalReadiness),
      value: metrics.organizationalReadiness,
      display: `${Math.round(metrics.organizationalReadiness)}%`,
    },
  ];

  // Get label position
  const getLabelPosition = (index: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    const labelRadius = maxRadius + 50;
    return {
      x: centerX + labelRadius * Math.cos(angle),
      y: centerY + labelRadius * Math.sin(angle),
    };
  };

  const assessmentColor = getAssessmentColor(metrics.assessment);

  return (
    <div className="bg-white rounded-2xl p-2 sm:p-8 shadow-xl border-2 border-blue-200">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-1">
          Preparation Model
        </h2>
        <p className="text-sm text-slate-600 mb-2">
          <strong>Committed to moving forward?</strong> Check your readiness.
        </p>
        <p className="text-xs text-slate-500 leading-relaxed">
          This model is for companies that are motivated and willing to put in
          the work to build long-term scaling systems. You have organizational
          backing and commitment—our assessment, problem identification, and
          solution architecture services ensure you have adequate timeline and
          budget while maximizing the benefits of your new systems, software,
          and AI solutions.
        </p>
      </div>

      <div className="flex justify-center">
        <svg
          width="450"
          height="450"
          viewBox="0 0 450 450"
          className="w-full h-auto"
        >
          <defs>
            <clipPath id="prepLabelClip">
              <rect x="-54" y="-20" width="108" height="40" rx="6" />
            </clipPath>
          </defs>

          {/* Grid circles */}
          {[20, 40, 60, 80, 100].map((percent) => {
            const radius = (percent / 100) * maxRadius;
            return (
              <circle
                key={percent}
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray={percent === 100 ? "none" : "3,3"}
              />
            );
          })}

          {/* Grid lines from center to vertices */}
          {[0, 1, 2, 3, 4].map((i) => {
            const vertex = getVertex(i, 100);
            return (
              <line
                key={i}
                x1={centerX}
                y1={centerY}
                x2={vertex.x}
                y2={vertex.y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}

          {/* Reference pentagon outline */}
          <path
            d={refPath}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="2"
            strokeDasharray="5,5"
          />

          {/* Data pentagon (filled) */}
          <path
            d={dataPath}
            fill={assessmentColor}
            fillOpacity="0.2"
            stroke={assessmentColor}
            strokeWidth="3"
            className="transition-all duration-500"
          />

          {/* Vertex points */}
          {dataVertices.map((vertex, index) => (
            <circle
              key={index}
              cx={vertex.x}
              cy={vertex.y}
              r="6"
              fill={labels[index].color}
              stroke="white"
              strokeWidth="2"
              className="transition-all duration-500"
            />
          ))}

          {/* Labels with icons and values */}
          {labels.map((label, index) => {
            const pos = getLabelPosition(index);

            return (
              <g key={index}>
                <rect
                  x={pos.x - 54}
                  y={pos.y - 20}
                  width="108"
                  height="40"
                  rx="6"
                  fill="white"
                  stroke={label.color}
                  strokeWidth="2"
                  opacity="0.95"
                />
                <g
                  clipPath="url(#prepLabelClip)"
                  transform={`translate(${pos.x}, ${pos.y})`}
                >
                  <text
                    x={0}
                    y={-3}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="600"
                    fill="#334155"
                  >
                    {label.icon} {label.text}
                  </text>
                  <text
                    x={0}
                    y={12}
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="900"
                    fill={label.color}
                  >
                    {label.display}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Center label - Assessment */}
          <circle
            cx={centerX}
            cy={centerY}
            r="45"
            fill="white"
            stroke={assessmentColor}
            strokeWidth="2.5"
          />
          <text
            x={centerX}
            y={centerY - 8}
            textAnchor="middle"
            className="text-[8px] font-semibold"
            fill="#64748b"
          >
            Assessment
          </text>
          <text
            x={centerX}
            y={centerY + 6}
            textAnchor="middle"
            className="text-[9px] font-black leading-tight"
            fill={assessmentColor}
          >
            <tspan x={centerX} dy="0">
              {metrics.assessment.split(" ")[0]}
            </tspan>
            <tspan x={centerX} dy="11">
              {metrics.assessment.split(" ").slice(1).join(" ")}
            </tspan>
          </text>
        </svg>
      </div>
    </div>
  );
};
